import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FlightBundle } from '../../interfaces/flightInterface';
import { AgentAdditionalInfo } from '../ShoppingCart/agentActions/AgentActions';

// Interface for bundle flights
export interface BundleFlightData extends Omit<FlightBundle, 'agent_additional_info'> {
  agent_additional_info?: AgentAdditionalInfo;
}

// Interface for dynamic (one-way) flights
export interface DynamicFlightData extends Omit<FlightBundle, 'agent_additional_info'> {
  agent_additional_info?: AgentAdditionalInfo;
}

// State for individual flight direction
export interface DirectionalFlightState {
  allFlights: DynamicFlightData[];
  filteredFlights: DynamicFlightData[];
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalCount: number;
  isLoading: boolean;
  error: string | null;
  optionalFilters: any;
}

export interface FlightSearchState {
  // Bundle flight search results from API
  allBundleFlights: BundleFlightData[];
  filteredBundleFlights: BundleFlightData[];

  // Dynamic flight search results organized by direction
  dynamicFlights: {
    flightFrom: DirectionalFlightState;
    flightBack: DirectionalFlightState;
  };

  // Pagination state for bundle flights
  bundleFlightCurrentPage: number;
  bundleFlightTotalPages: number;
  bundleFlightItemsPerPage: number;
  bundleFlightTotalCount: number;

  // Search and filter states
  flightDates: string;
  isLoadingBundleFlights: boolean;
  errorBundleFlights: string | null;

  // UI states
  selectedFlightId: string | null;
  viewMode: 'grid' | 'list';

  // Optional filters from API for bundle flights
  bundleFlightOptionalFilters: any;
}

export const ITEMS_PER_PAGE = 30;

// Helper function to create initial directional flight state
const createInitialDirectionalState = (): DirectionalFlightState => ({
  allFlights: [],
  filteredFlights: [],
  currentPage: 1,
  totalPages: 0,
  itemsPerPage: ITEMS_PER_PAGE,
  totalCount: 0,
  isLoading: false,
  error: null,
  optionalFilters: null,
});

const initialState: FlightSearchState = {
  allBundleFlights: [],
  filteredBundleFlights: [],

  dynamicFlights: {
    flightFrom: createInitialDirectionalState(),
    flightBack: createInitialDirectionalState(),
  },

  bundleFlightCurrentPage: 1,
  bundleFlightTotalPages: 0,
  bundleFlightItemsPerPage: ITEMS_PER_PAGE,
  bundleFlightTotalCount: 0,

  flightDates: '',
  isLoadingBundleFlights: false,
  errorBundleFlights: null,

  selectedFlightId: null,
  viewMode: 'grid',

  bundleFlightOptionalFilters: null,
};

export const flightSlice = createSlice({
  name: 'flights',
  initialState,
  reducers: {
    // Bundle Flight Actions
    setBundleFlights: (state, action: PayloadAction<BundleFlightData[]>) => {
      state.allBundleFlights = action.payload;
      state.filteredBundleFlights = action.payload;
      state.bundleFlightTotalCount = action.payload.length;
      state.bundleFlightTotalPages = Math.ceil(action.payload.length / state.bundleFlightItemsPerPage);
      state.bundleFlightCurrentPage = 1; // Reset to first page when new data arrives
      state.errorBundleFlights = null;
    },

    setFilteredBundleFlights: (state, action: PayloadAction<BundleFlightData[]>) => {
      state.filteredBundleFlights = action.payload;
      state.bundleFlightTotalCount = action.payload.length;
      state.bundleFlightTotalPages = Math.ceil(action.payload.length / state.bundleFlightItemsPerPage);
      state.bundleFlightCurrentPage = 1; // Reset to first page when filters change
    },

    setBundleFlightCurrentPage: (state, action: PayloadAction<number>) => {
      state.bundleFlightCurrentPage = action.payload;
    },

    setBundleFlightItemsPerPage: (state, action: PayloadAction<number>) => {
      state.bundleFlightItemsPerPage = action.payload;
      state.bundleFlightTotalPages = Math.ceil(state.bundleFlightTotalCount / action.payload);
      state.bundleFlightCurrentPage = 1; // Reset to first page when changing items per page
    },

    setBundleFlightLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoadingBundleFlights = action.payload;
      if (action.payload) {
        state.errorBundleFlights = null; // Clear error when starting new request
      }
    },

    setBundleFlightError: (state, action: PayloadAction<string>) => {
      state.errorBundleFlights = action.payload;
      state.isLoadingBundleFlights = false;
    },

    clearBundleFlightError: (state) => {
      state.errorBundleFlights = null;
    },

    setBundleFlightOptionalFilters: (state, action: PayloadAction<any>) => {
      state.bundleFlightOptionalFilters = action.payload;
    },

    // Dynamic Flight Actions - Directional
    setDynamicFlights: (
      state,
      action: PayloadAction<{
        direction: 'flightFrom' | 'flightBack';
        flights: DynamicFlightData[]
      }>
    ) => {
      const { direction, flights } = action.payload;
      const directionState = state.dynamicFlights[direction];

      directionState.allFlights = flights;
      directionState.filteredFlights = flights;
      directionState.totalCount = flights.length;
      directionState.totalPages = Math.ceil(flights.length / directionState.itemsPerPage);
      directionState.currentPage = 1; // Reset to first page when new data arrives
      directionState.error = null;
    },

    setFilteredDynamicFlights: (
      state,
      action: PayloadAction<{
        direction: 'flightFrom' | 'flightBack';
        flights: DynamicFlightData[]
      }>
    ) => {
      const { direction, flights } = action.payload;
      const directionState = state.dynamicFlights[direction];

      directionState.filteredFlights = flights;
      directionState.totalCount = flights.length;
      directionState.totalPages = Math.ceil(flights.length / directionState.itemsPerPage);
      directionState.currentPage = 1; // Reset to first page when filters change
    },

    setDynamicFlightCurrentPage: (
      state,
      action: PayloadAction<{
        direction: 'flightFrom' | 'flightBack';
        page: number
      }>
    ) => {
      const { direction, page } = action.payload;
      state.dynamicFlights[direction].currentPage = page;
    },

    setDynamicFlightItemsPerPage: (
      state,
      action: PayloadAction<{
        direction: 'flightFrom' | 'flightBack';
        itemsPerPage: number
      }>
    ) => {
      const { direction, itemsPerPage } = action.payload;
      const directionState = state.dynamicFlights[direction];

      directionState.itemsPerPage = itemsPerPage;
      directionState.totalPages = Math.ceil(directionState.totalCount / itemsPerPage);
      directionState.currentPage = 1; // Reset to first page when changing items per page
    },

    setDynamicFlightLoading: (
      state,
      action: PayloadAction<{
        direction: 'flightFrom' | 'flightBack';
        isLoading: boolean
      }>
    ) => {
      const { direction, isLoading } = action.payload;
      state.dynamicFlights[direction].isLoading = isLoading;
      if (isLoading) {
        state.dynamicFlights[direction].error = null; // Clear error when starting new request
      }
    },

    setDynamicFlightError: (
      state,
      action: PayloadAction<{
        direction: 'flightFrom' | 'flightBack';
        error: string
      }>
    ) => {
      const { direction, error } = action.payload;
      state.dynamicFlights[direction].error = error;
      state.dynamicFlights[direction].isLoading = false;
    },

    clearDynamicFlightError: (
      state,
      action: PayloadAction<'flightFrom' | 'flightBack'>
    ) => {
      const direction = action.payload;
      state.dynamicFlights[direction].error = null;
    },

    setDynamicFlightOptionalFilters: (
      state,
      action: PayloadAction<{
        direction: 'flightFrom' | 'flightBack';
        filters: any
      }>
    ) => {
      const { direction, filters } = action.payload;
      state.dynamicFlights[direction].optionalFilters = filters;
    },

    // Common Flight Actions
    updateFlightDates: (state, action: PayloadAction<string>) => {
      state.flightDates = action.payload;
    },

    setSelectedFlight: (state, action: PayloadAction<string | null>) => {
      state.selectedFlightId = action.payload;
    },

    setViewMode: (state, action: PayloadAction<'grid' | 'list'>) => {
      state.viewMode = action.payload;
    },

    // Reset all flight data
    resetFlightState: (state) => {
      return initialState;
    },

    // Reset only bundle flight state
    resetBundleFlightState: (state) => {
      state.allBundleFlights = [];
      state.filteredBundleFlights = [];
      state.bundleFlightCurrentPage = 1;
      state.bundleFlightTotalPages = 0;
      state.bundleFlightTotalCount = 0;
      state.isLoadingBundleFlights = false;
      state.errorBundleFlights = null;
      state.bundleFlightOptionalFilters = null;
    },

    // Reset only dynamic flight state
    resetDynamicFlightState: (
      state,
      action: PayloadAction<'flightFrom' | 'flightBack' | 'both'>
    ) => {
      const direction = action.payload;

      if (direction === 'both') {
        state.dynamicFlights.flightFrom = createInitialDirectionalState();
        state.dynamicFlights.flightBack = createInitialDirectionalState();
      } else {
        state.dynamicFlights[direction] = createInitialDirectionalState();
      }
    },

    // Add a single bundle flight (useful for dynamic updates)
    addBundleFlight: (state, action: PayloadAction<BundleFlightData>) => {
      state.allBundleFlights.push(action.payload);
      state.filteredBundleFlights.push(action.payload);
      state.bundleFlightTotalCount = state.filteredBundleFlights.length;
      state.bundleFlightTotalPages = Math.ceil(state.bundleFlightTotalCount / state.bundleFlightItemsPerPage);
    },

    // Add a single dynamic flight (useful for dynamic updates)
    addDynamicFlight: (
      state,
      action: PayloadAction<{
        direction: 'flightFrom' | 'flightBack';
        flight: DynamicFlightData
      }>
    ) => {
      const { direction, flight } = action.payload;
      const directionState = state.dynamicFlights[direction];

      directionState.allFlights.push(flight);
      directionState.filteredFlights.push(flight);
      directionState.totalCount = directionState.filteredFlights.length;
      directionState.totalPages = Math.ceil(directionState.totalCount / directionState.itemsPerPage);
    },

    // Update a specific bundle flight
    updateBundleFlight: (
      state,
      action: PayloadAction<{ identifier: string; flight: Partial<BundleFlightData> }>
    ) => {
      const { identifier, flight } = action.payload;

      const allFlightIndex = state.allBundleFlights.findIndex(
        (f) => f.identifier === identifier
      );
      if (allFlightIndex !== -1) {
        state.allBundleFlights[allFlightIndex] = {
          ...state.allBundleFlights[allFlightIndex],
          ...flight,
        };
      }

      const filteredFlightIndex = state.filteredBundleFlights.findIndex(
        (f) => f.identifier === identifier
      );
      if (filteredFlightIndex !== -1) {
        state.filteredBundleFlights[filteredFlightIndex] = {
          ...state.filteredBundleFlights[filteredFlightIndex],
          ...flight,
        };
      }
    },

    // Update a specific dynamic flight
    updateDynamicFlight: (
      state,
      action: PayloadAction<{
        direction: 'flightFrom' | 'flightBack';
        identifier: string;
        flight: Partial<DynamicFlightData>
      }>
    ) => {
      const { direction, identifier, flight } = action.payload;
      const directionState = state.dynamicFlights[direction];

      const allFlightIndex = directionState.allFlights.findIndex(
        (f) => f.identifier === identifier
      );
      if (allFlightIndex !== -1) {
        directionState.allFlights[allFlightIndex] = {
          ...directionState.allFlights[allFlightIndex],
          ...flight,
        };
      }

      const filteredFlightIndex = directionState.filteredFlights.findIndex(
        (f) => f.identifier === identifier
      );
      if (filteredFlightIndex !== -1) {
        directionState.filteredFlights[filteredFlightIndex] = {
          ...directionState.filteredFlights[filteredFlightIndex],
          ...flight,
        };
      }
    },

    // Remove a bundle flight
    removeBundleFlight: (state, action: PayloadAction<string>) => {
      const identifier = action.payload;
      state.allBundleFlights = state.allBundleFlights.filter(
        (f) => f.identifier !== identifier
      );
      state.filteredBundleFlights = state.filteredBundleFlights.filter(
        (f) => f.identifier !== identifier
      );
      state.bundleFlightTotalCount = state.filteredBundleFlights.length;
      state.bundleFlightTotalPages = Math.ceil(state.bundleFlightTotalCount / state.bundleFlightItemsPerPage);

      // Adjust current page if needed
      if (state.bundleFlightCurrentPage > state.bundleFlightTotalPages && state.bundleFlightTotalPages > 0) {
        state.bundleFlightCurrentPage = state.bundleFlightTotalPages;
      }
    },

    // Remove a dynamic flight
    removeDynamicFlight: (
      state,
      action: PayloadAction<{
        direction: 'flightFrom' | 'flightBack';
        identifier: string
      }>
    ) => {
      const { direction, identifier } = action.payload;
      const directionState = state.dynamicFlights[direction];

      directionState.allFlights = directionState.allFlights.filter(
        (f) => f.identifier !== identifier
      );
      directionState.filteredFlights = directionState.filteredFlights.filter(
        (f) => f.identifier !== identifier
      );
      directionState.totalCount = directionState.filteredFlights.length;
      directionState.totalPages = Math.ceil(directionState.totalCount / directionState.itemsPerPage);

      // Adjust current page if needed
      if (directionState.currentPage > directionState.totalPages && directionState.totalPages > 0) {
        directionState.currentPage = directionState.totalPages;
      }
    },
  },
});

export const {
  // Bundle Flight Actions
  setBundleFlights,
  setFilteredBundleFlights,
  setBundleFlightCurrentPage,
  setBundleFlightItemsPerPage,
  setBundleFlightLoading,
  setBundleFlightError,
  clearBundleFlightError,
  setBundleFlightOptionalFilters,

  // Dynamic Flight Actions
  setDynamicFlights,
  setFilteredDynamicFlights,
  setDynamicFlightCurrentPage,
  setDynamicFlightItemsPerPage,
  setDynamicFlightLoading,
  setDynamicFlightError,
  clearDynamicFlightError,
  setDynamicFlightOptionalFilters,

  // Common Actions
  updateFlightDates,
  setSelectedFlight,
  setViewMode,
  resetFlightState,
  resetBundleFlightState,
  resetDynamicFlightState,
  addBundleFlight,
  addDynamicFlight,
  updateBundleFlight,
  updateDynamicFlight,
  removeBundleFlight,
  removeDynamicFlight,
} = flightSlice.actions;

export default flightSlice.reducer;

// Selectors for Bundle Flights
export const selectAllBundleFlights = (state: { flights: FlightSearchState }) =>
  state.flights.allBundleFlights;

export const selectFilteredBundleFlights = (state: { flights: FlightSearchState }) =>
  state.flights.filteredBundleFlights;

export const selectBundleFlightCurrentPage = (state: { flights: FlightSearchState }) =>
  state.flights.bundleFlightCurrentPage;

export const selectBundleFlightItemsPerPage = (state: { flights: FlightSearchState }) =>
  state.flights.bundleFlightItemsPerPage;

export const selectBundleFlightTotalCount = (state: { flights: FlightSearchState }) =>
  state.flights.bundleFlightTotalCount;

export const selectBundleFlightTotalPages = (state: { flights: FlightSearchState }) =>
  state.flights.bundleFlightTotalPages;

export const selectBundleFlightIsLoading = (state: { flights: FlightSearchState }) =>
  state.flights.isLoadingBundleFlights;

export const selectBundleFlightError = (state: { flights: FlightSearchState }) =>
  state.flights.errorBundleFlights;

export const selectBundleFlightOptionalFilters = (state: { flights: FlightSearchState }) =>
  state.flights.bundleFlightOptionalFilters;

// Selectors for Dynamic Flights - Directional
export const selectAllDynamicFlights = (direction: 'flightFrom' | 'flightBack') =>
  (state: { flights: FlightSearchState }) => state.flights.dynamicFlights[direction].allFlights;

export const selectFilteredDynamicFlights = (direction: 'flightFrom' | 'flightBack') =>
  (state: { flights: FlightSearchState }) => state.flights.dynamicFlights[direction].filteredFlights;

export const selectDynamicFlightCurrentPage = (direction: 'flightFrom' | 'flightBack') =>
  (state: { flights: FlightSearchState }) => state.flights.dynamicFlights[direction].currentPage;

export const selectDynamicFlightItemsPerPage = (direction: 'flightFrom' | 'flightBack') =>
  (state: { flights: FlightSearchState }) => state.flights.dynamicFlights[direction].itemsPerPage;

export const selectDynamicFlightTotalCount = (direction: 'flightFrom' | 'flightBack') =>
  (state: { flights: FlightSearchState }) => state.flights.dynamicFlights[direction].totalCount;

export const selectDynamicFlightTotalPages = (direction: 'flightFrom' | 'flightBack') =>
  (state: { flights: FlightSearchState }) => state.flights.dynamicFlights[direction].totalPages;

export const selectDynamicFlightIsLoading = (direction: 'flightFrom' | 'flightBack') =>
  (state: { flights: FlightSearchState }) => state.flights.dynamicFlights[direction].isLoading;

export const selectDynamicFlightError = (direction: 'flightFrom' | 'flightBack') =>
  (state: { flights: FlightSearchState }) => state.flights.dynamicFlights[direction].error;

export const selectDynamicFlightOptionalFilters = (direction: 'flightFrom' | 'flightBack') =>
  (state: { flights: FlightSearchState }) => state.flights.dynamicFlights[direction].optionalFilters;

// Common Selectors
export const selectFlightDates = (state: { flights: FlightSearchState }) =>
  state.flights.flightDates;

export const selectSelectedFlightId = (state: { flights: FlightSearchState }) =>
  state.flights.selectedFlightId;

export const selectViewMode = (state: { flights: FlightSearchState }) =>
  state.flights.viewMode;

// Computed selectors for paginated data
export const selectPaginatedBundleFlights = (state: { flights: FlightSearchState }) => {
  const { filteredBundleFlights, bundleFlightCurrentPage, bundleFlightItemsPerPage } = state.flights;
  const startIndex = (bundleFlightCurrentPage - 1) * bundleFlightItemsPerPage;
  const endIndex = startIndex + bundleFlightItemsPerPage;
  return filteredBundleFlights.slice(startIndex, endIndex);
};

export const selectPaginatedDynamicFlights = (direction: 'flightFrom' | 'flightBack') =>
  (state: { flights: FlightSearchState }) => {
    const directionState = state.flights.dynamicFlights[direction];
    const startIndex = (directionState.currentPage - 1) * directionState.itemsPerPage;
    const endIndex = startIndex + directionState.itemsPerPage;
    return directionState.filteredFlights.slice(startIndex, endIndex);
  };

export const selectSelectedBundleFlight = (state: { flights: FlightSearchState }) =>
  state.flights.allBundleFlights.find(
    (flight) => flight.identifier === state.flights.selectedFlightId
  ) || null;

export const selectSelectedDynamicFlight = (direction: 'flightFrom' | 'flightBack') =>
  (state: { flights: FlightSearchState }) =>
    state.flights.dynamicFlights[direction].allFlights.find(
      (flight) => flight.identifier === state.flights.selectedFlightId
    ) || null;